import { isKeycloakEnabled } from '@/auth';
import { Account } from '@/client/accounts';
import { selectCurrentAccount } from '@/store/features/account';
import { isDemoModeActive } from '@/utils/demo-mode';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useAppSelector } from './store';

type ServiceUrlType =
  | ((param: string, accountId: string) => string)
  | ((param: string) => string)
  | (() => string);

type UseNotificationsOptions = {
  queueAll?: boolean;
};

export const useNotifications = (
  serviceUrl: ServiceUrlType,
  options?: UseNotificationsOptions,
) => {
  const isKeycloakAuth = isKeycloakEnabled();
  const { user } = useAuth();
  const currentAccount = useAppSelector(selectCurrentAccount);

  const [notificationParam, setNotificationParam] = useState<
    string | undefined
  >(undefined);
  const [lastMessage, setLastMessage] = useState<{
    event: string;
    data: any;
    lastEventId: string;
  } | null>(null);
  const isConnected = useRef(false);
  const messagesQueueRef = useRef<
    { event: string; data: any; lastEventId: string }[]
  >([]);

  useEffect(() => {
    if (!notificationParam) return;

    const enableQueue = options?.queueAll ?? false;

    const headers: Record<string, string> = {};
    const demoModeActive = isDemoModeActive();
    const demoBearerToken = process.env.REACT_APP_DEMO_BEARER_TOKEN;
    const demoAccountId = process.env.REACT_APP_DEMO_ACCOUNT_ID;

    if (demoModeActive && demoBearerToken && demoAccountId) {
      headers['Authorization'] = `Bearer ${demoBearerToken}`;
    } else if (isKeycloakAuth) {
      headers['Authorization'] = `Bearer ${user?.access_token}`;
    }

    const sourcePolyfill = new EventSourcePolyfill(
      serviceUrl(notificationParam, (currentAccount as Account)?.id),
      {
        withCredentials: true,
        headers,
      },
    );

    // Check that a connection is established
    sourcePolyfill.onopen = () => {
      isConnected.current = true;
    };

    sourcePolyfill.onmessage = (message) => {
      isConnected.current = true;
      const data = JSON.parse(message.data);
      const wrapped = { ...message, data } as any;
      setLastMessage(wrapped);

      if (enableQueue) {
        // Avoid losing data when events are received in a burst.
        messagesQueueRef.current.push(wrapped);
      }
    };

    // If an error is encountered during connection the Eventsource will set the connection state to false, but will continue to try to connect
    sourcePolyfill.onerror = () => {
      isConnected.current = false;
    };

    return () => {
      sourcePolyfill.close();
      isConnected.current = false;
      setLastMessage(null);
      messagesQueueRef.current = [];
    };
  }, [notificationParam, options?.queueAll]);

  return {
    lastMessage,
    notificationParam,
    setNotificationParam,
    isConnected,
    messagesQueueRef,
  };
};
